import { useState, useCallback, useRef } from "react";
import { captureException } from "@sentry/react";

// Custom hook to manage SSE connection
export default function useSSE(url: string) {
  // Store received data (only most recent message)
  const [data, setData] = useState<unknown>();

  // Store error state (connection failures, parsing errors, etc.)
  const [error, setError] = useState<String | null>(null);

  // Connection status (true: connected, false: disconnected/failed)
  const [isConnected, setIsConnected] = useState(false);

  // EventSource object reference - maintains same object across re-renders
  const eventSourceRef = useRef<EventSource | null>(null);

  // Reconnection timer reference - for cleanup on component unmount
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SSE connection function
  // useCallback memoizes function - creates new one only when dependencies (url) change
  const connect = useCallback(() => {
    try {
      // Close existing connection if present
      // Prevents duplicate connections before establishing new one
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Create new EventSource object
      // EventSource: built-in browser API for managing SSE connections
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Event handler for successful connection
      // onopen: executed when EventSource successfully connects to server
      eventSource.onopen = () => {
        console.log("SSE connection opened");
        setIsConnected(true); // Update connection status
        setError(null); // Clear previous error state
      };

      // Event handler for message reception
      // onmessage: executed whenever server sends "data:" format message
      eventSource.onmessage = (event) => {
        try {
          // event.data: actual data sent by server (string format)
          // JSON.parse: convert string to JavaScript object
          const parsedData = JSON.parse(event.data);
          setData(parsedData); // Store parsed data in state
        } catch (parseError) {
          // Handle JSON parsing failure
          console.error("Error parsing SSE data:", parseError);
          captureException(parseError);
          setError("Data parsing error");
        }
      };

      // Event handler for errors
      // onerror: executed when connection fails, network errors occur, etc.
      eventSource.onerror = (event) => {
        console.error("SSE connection error:", event);
        captureException(new Error("SSE connection error"));
        setIsConnected(false); // Change connection status to false

        // Attempt auto-reconnection if connection is completely closed
        // EventSource.CLOSED: permanently closed connection state (value: 2)
        if (eventSource.readyState === EventSource.CLOSED) {
          setError("Connection lost. Attempting to reconnect...");

          // Retry connection after 3 seconds (prevents server overload from frequent attempts)
          reconnectTimeoutRef.current = setTimeout(() => {
            connect(); // Recursive call to retry connection
          }, 3000);
        }
      };
    } catch (err) {
      // Handle EventSource object creation failure
      console.error("Error creating SSE connection:", err);
      captureException(err);
      setError("Connection creation error");
    }
  }, [url]); // Create new connect function when url changes

  // Disconnect function
  const disconnect = useCallback(() => {
    // Close EventSource connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close(); // Close connection
      eventSourceRef.current = null; // Reset reference
    }

    // Cancel reconnection timer if running
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    setIsConnected(false); // Update connection status
  }, []);

  // Return values and functions for external use
  return {
    data, // Latest received data
    error, // Error message
    isConnected, // Connection status
    connect, // Manual connection function
    disconnect, // Manual disconnect function
  };
}
